import React from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";

import useStyles from "./styles";

import {Project, ProjectId} from "common/types/project";
import {OrganisationId} from "common/types/organisation";

interface IssueResponseProjectSelectorProps {
  projects: Project[];
  projectIds: ProjectId[];
  setProjectIds: (ids: ProjectId[]) => void;
  currentProjectId: ProjectId;
  currentProjectName: string;
  organisationId: OrganisationId;
}

export default function IssueResponseProjectSelector({
  projects,
  projectIds,
  setProjectIds,
  currentProjectId,
  currentProjectName,
  organisationId,
}: IssueResponseProjectSelectorProps) {
  const classes = useStyles();

  const isProjectSpecific = projectIds.length > 0;

  const useRegionTerm = organisationId === 74;
  const term = useRegionTerm ? "region" : "project";

  const handleAvailabilityChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value === "specific") {
      setProjectIds([currentProjectId]);
    } else {
      setProjectIds([]);
    }
  };

  const handleProjectsChange = (_: unknown, selectedProjects: Project[]) => {
    const newProjectIds = selectedProjects.map(project => project.id);
    setProjectIds([currentProjectId, ...newProjectIds]);
  };

  return (
    <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
      <div className={classes.fieldHeadingText}>
        {useRegionTerm ? "Regions" : "Projects"}
      </div>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label={`${term} availability`}
          name={`${term}-availability`}
          value={isProjectSpecific ? "specific" : "everywhere"}
          onChange={handleAvailabilityChange}
        >
          <FormControlLabel
            value="everywhere"
            control={<Radio />}
            label="Available everywhere"
            classes={{label: classes.label}}
          />
          <FormControlLabel
            value="specific"
            control={<Radio />}
            label={`Make specific to this ${term} (${currentProjectName})`}
            classes={{label: classes.label}}
          />
        </RadioGroup>
      </FormControl>

      {isProjectSpecific && (
        <Autocomplete
          size="small"
          multiple
          options={projects}
          disableCloseOnSelect
          getOptionLabel={option => option.name}
          renderOption={(option, {selected}) => (
            <>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{marginRight: "8px"}}
                checked={selected}
              />
              {option.name}
            </>
          )}
          value={projects.filter(project => projectIds.includes(project.id))}
          onChange={handleProjectsChange}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={`Additional ${term}s`}
              placeholder={`Select additional ${term}s`}
              style={{marginTop: "4px"}}
            />
          )}
        />
      )}
    </div>
  );
}
